import { useMutation, useQuery } from '@tanstack/react-query';
import { usePathname } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useState, useRef, useEffect } from 'react';

import { MobileNumberSetupForm } from '@/components/MobileNumberSetupForm';
import { MobileNumberSetupSheet } from '@/components/MobileNumberSetupSheet';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { INQUIRY_SUCCESS_SHEET_COPY } from '@/constants/smsPromptCopy';
import { useEnablePush } from '@/hooks/useEnablePush';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import {
  applyCalloutAction,
  CALLOUT_KEYS,
  getUserCalloutsQueryOptions,
  selectCallout,
  shouldSuppressCallout,
} from '@/services/userCallouts';
import { segmentClient } from '@/util/analytics';
import {
  getPushNotificationPermission,
  subscribeToPushPermissionChange,
} from '@/util/pushNotifications';
import { isEHRSession } from '@/util/smartLaunch';

const isPathnameSuccess = (pathname: string) => pathname === '/directory/inquiry/success';

// `/inbox` is where the success screen's close button lands, and the success
// screen picks its forward CTA at random, so leaving it out meant a submitter
// who closed the screen was never asked.
const MODALWORTHY_PATHS = ['/community', '/directory', '/inbox'];

const isPathnameModalworthy = (pathname: string) => MODALWORTHY_PATHS.includes(pathname);

/**
 * Picks the body for the channels the sheet is actually rendering.
 *
 * The two sections gate separately, so the body cannot be written for SMS and
 * left to stand over whatever appears: a submitter who already has texts sees
 * a lone push button, and a pitch about a quick text would name a form that is
 * not there.
 *
 * The `sms` fallback also covers the case where neither shows, which never
 * renders — the sheet is closed unless one of them does.
 */
function selectSheetBody(showSms: boolean, showPush: boolean) {
  if (showSms && showPush) {
    return INQUIRY_SUCCESS_SHEET_COPY.body.both;
  }

  return showPush ? INQUIRY_SUCCESS_SHEET_COPY.body.push : INQUIRY_SUCCESS_SHEET_COPY.body.sms;
}

/**
 * Opt-in sheet shown after an inquiry is submitted, when the submitter lands on
 * an eligible screen and a reply would not reach them. Owns only the gating and
 * chrome; the phone field, validation, save, and SMS-preference enable are
 * delegated to the shared `MobileNumberSetupForm` so it stays consistent with
 * the expert prompts, and the push ask goes through `useEnablePush` so it is
 * measured alongside every other push surface.
 *
 * Both channels are asked in one sheet rather than two prompts. Nothing
 * arbitrates between prompts mounted at the root, so two eligible in the same
 * frame would be ordered by mount rather than priority — the fix is to not
 * have two.
 *
 * The two channels are gated separately, since a user can already have one and
 * not the other. Opt-out is a server-backed callout per channel, so the choice
 * follows the user across devices; "Not now" persists nothing and stays
 * eligible to re-show. Fail-closed: it shows only once the callouts query has
 * loaded and the callout is unresolved, so an outage never re-prompts a user
 * who opted out.
 *
 * Push resolves its callout once the permission dialog has actually been
 * raised, whichever way it went, because that ask is not repeatable — browsers
 * block re-prompting and escalate repeat dismissals to an auto-deny. Skipping
 * the sheet without opening the dialog spends nothing and stays eligible.
 */
export default function SMSNotificationModal() {
  const currentPathname = usePathname();

  const referrerPath = useRef<string | null>(null);

  const [modalworthy, setModalworthy] = useState(false);

  useEffect(() => {
    const referrerPathname = referrerPath.current;

    if (!referrerPathname) {
      referrerPath.current = currentPathname;

      return;
    }

    if (isPathnameSuccess(referrerPathname) && isPathnameModalworthy(currentPathname)) {
      setModalworthy(true);
    }

    if (currentPathname !== referrerPathname) {
      referrerPath.current = currentPathname;
    }
  }, [currentPathname]);

  useEffect(() => {
    if (!isPathnameModalworthy(currentPathname)) {
      setModalworthy(false);
    }
  }, [currentPathname]);

  const [visible, setVisible] = useState(true);

  const userId = useAtomValue(userIdAtom);

  const { data: userData } = useQuery(getCurrentUserV2QueryOptions(userId));

  const { data: callouts, isSuccess: calloutsLoaded } = useQuery(
    getUserCalloutsQueryOptions(userId)
  );

  const smsPromptDisabled = shouldSuppressCallout(
    calloutsLoaded,
    selectCallout(callouts, CALLOUT_KEYS.INQUIRY_SUCCESS_SMS)
  );

  const pushPromptDisabled = shouldSuppressCallout(
    calloutsLoaded,
    selectCallout(callouts, CALLOUT_KEYS.INQUIRY_SUCCESS_PUSH)
  );

  const mobilePhoneSliced = userData?.mobilePhone?.replace('+1', '') || '';

  const newMessageSmsPermissionGranted = userData?.notificationsPreferences?.newMessageSms;

  const smsLacking = !newMessageSmsPermissionGranted || mobilePhoneSliced === '';

  const [pushGranted, setPushGranted] = useState<boolean | null>(null);

  /**
   * Read the permission when the sheet becomes relevant, not only on mount.
   *
   * The provider SDK initializes asynchronously and is skipped entirely on
   * localhost unless opted in, so a read taken at mount can land before it is
   * ready. Re-reading when the sheet arms is what stops a slow init from
   * deciding whether the user is ever asked.
   *
   * A read that throws is treated as "not granted" rather than left unknown:
   * suppressing the ask on an error hides it with no trace, while showing it
   * costs a user who already granted push one redundant button.
   */
  useEffect(() => {
    let active = true;

    // Web returns a boolean and native a promise; awaiting covers both.
    void (async () => {
      let granted = false;

      try {
        granted = Boolean(await getPushNotificationPermission());
      } catch {
        granted = false;
      }

      if (active) {
        setPushGranted(granted);
      }
    })();

    return () => {
      active = false;
    };
  }, [modalworthy]);

  useEffect(() => subscribeToPushPermissionChange(setPushGranted), []);

  // Null only until the first read resolves. Treated as "no gap" so the sheet
  // cannot flash a push ask at someone who already granted it.
  const pushLacking = pushGranted === false;

  const showSms = smsLacking && !smsPromptDisabled;

  const showPush = pushLacking && !pushPromptDisabled;

  const enablePush = useEnablePush('inquiry_success');

  const resolvePromptMutation = useMutation({
    mutationFn: () => applyCalloutAction(CALLOUT_KEYS.INQUIRY_SUCCESS_SMS, 'resolve'),
  });

  const resolvePushPromptMutation = useMutation({
    mutationFn: () => applyCalloutAction(CALLOUT_KEYS.INQUIRY_SUCCESS_PUSH, 'resolve'),
  });

  const onClose = () => {
    setVisible(false);
  };

  const onDisablePrompt = () => {
    segmentClient.track(ANALYTICS_EVENTS.SMS_PROMPT_OPTED_OUT);

    resolvePromptMutation.mutate();

    setVisible(false);
  };

  const onEnablePush = async () => {
    await enablePush();

    // Resolved whichever way the dialog went: the permission is spent either
    // way, so re-asking later can only annoy or be silently auto-denied.
    resolvePushPromptMutation.mutate();

    setVisible(false);
  };

  const isOpen =
    visible &&
    (showSms || showPush) &&
    modalworthy &&
    isPathnameModalworthy(currentPathname) &&
    !isEHRSession();

  useEffect(() => {
    if (isOpen) {
      segmentClient.track(ANALYTICS_EVENTS.SMS_PROMPT_SHOWN, {
        pathname: currentPathname,
        hasPhoneOnFile: Boolean(userData?.mobilePhone),
      });
    }
  }, [isOpen, currentPathname, userData?.mobilePhone]);

  return (
    <MobileNumberSetupSheet isOpen={isOpen} onClose={onClose}>
      <VStack space="md">
        <Heading size="h2-xl">{INQUIRY_SUCCESS_SHEET_COPY.heading}</Heading>

        <Text>{selectSheetBody(showSms, showPush)}</Text>

        {showPush && (
          <Button onPress={onEnablePush} disabled={resolvePushPromptMutation.isPending}>
            <ButtonText>{INQUIRY_SUCCESS_SHEET_COPY.pushAction}</ButtonText>
          </Button>
        )}

        {showSms && (
          <MobileNumberSetupForm
            source="inquiry_success"
            showHeader={false}
            enableSmsOnSave
            requireDirty={false}
            defaultMobilePhone={mobilePhoneSliced}
            saveLabel="Turn on texts"
            onComplete={() => {
              segmentClient.track(ANALYTICS_EVENTS.SMS_PROMPT_ENABLED);

              onClose();
            }}
            onSkip={() => {
              segmentClient.track(ANALYTICS_EVENTS.SMS_PROMPT_SKIPPED);

              onClose();
            }}
            onOptOut={onDisablePrompt}
          />
        )}
      </VStack>
    </MobileNumberSetupSheet>
  );
}
